import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconClose, IconAvatar, IconMail } from '@components/icons';
import { IconButton } from '@components/IconButton';
import { Input } from '@components/Input';
import { Button } from '@components/Button';
import styles from './AddContactModal.module.scss';

export interface AddContactData {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
}

export interface AddContactModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Close handler */
  onClose: () => void;

  /** Called when user clicks Add with valid data. Returns a promise so the modal can show loading state. */
  onAdd?: (data: AddContactData) => Promise<void> | void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Inner form component that mounts/unmounts with the modal,
 * ensuring state resets naturally on each open.
 */
const AddContactForm = ({
  onClose,
  onAdd,
}: Pick<AddContactModalProps, 'onClose' | 'onAdd'>) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFirstNameValid = firstName.trim().length > 0;
  const isLastNameValid = lastName.trim().length > 0;
  const isTitleValid = title.trim().length > 0;
  const isEmailValid = EMAIL_REGEX.test(email.trim());
  const isFormValid = isFirstNameValid && isLastNameValid && isTitleValid && isEmailValid;

  const handleAdd = async () => {
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onAdd?.({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        title: title.trim(),
        email: email.trim(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers */}
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-contact-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="add-contact-title" className={styles.title}>
            {t('addContact.title')}
          </h2>
          <IconButton
            size="small"
            aria-label={t('addContact.closeModal')}
            onClick={onClose}
            className={styles.closeButton}
          >
            <IconClose size={18} />
          </IconButton>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.nameRow}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('addContact.firstName')}</span>
              <Input
                value={firstName}
                onChange={setFirstName}
                placeholder={t('addContact.firstNamePlaceholder')}
                clearable
                leadingIcon={<IconAvatar size={18} />}
                className={styles.input}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('addContact.lastName')}</span>
              <Input
                value={lastName}
                onChange={setLastName}
                placeholder={t('addContact.lastNamePlaceholder')}
                clearable
                className={styles.input}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t('addContact.jobTitle')}</span>
            <Input
              value={title}
              onChange={setTitle}
              placeholder={t('addContact.jobTitlePlaceholder')}
              clearable
              className={styles.input}
              disabled={isSubmitting}
            />
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t('addContact.emailAddress')}</span>
            <Input
              value={email}
              onChange={setEmail}
              placeholder={t('addContact.emailPlaceholder')}
              type="email"
              clearable
              leadingIcon={<IconMail size={18} />}
              className={styles.input}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleAdd} disabled={!isFormValid || isSubmitting}>
            {isSubmitting ? t('addContact.adding') : t('common.add')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AddContactModal = ({ isOpen, onClose, onAdd }: AddContactModalProps) => {
  if (!isOpen) return null;
  return <AddContactForm onClose={onClose} onAdd={onAdd} />;
};
